import { useMemo, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material';
import { GlobalStyles } from '@mui/system';
import { globalStyles } from './theme';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { versionConfig, type AppVersion } from '@/config/versions';
import NewLoginPage from '@/pages/Login/NewLoginPage';
import RegisterPage from '@/pages/Register/RegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import { useTheme } from './contexts/ThemeContext';
import { GoogleAuthProviderWrapper } from './providers/GoogleAuthProvider';
import { FilterProvider } from '@/contexts/FilterContext';
import { DateRangeProvider } from '@/contexts/DateRangeContext';
import { UserProfileProvider } from '@/contexts/UserProfileContext';
import TermsAndConditionsPage from './pages/TermsAndConditions/TermsAndConditionsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicy/PrivacyPolicyPage';

// V11 Pages - Felton Intelligence
import { V11Layout } from './pages/v11/V11Layout';
import { V11Routes } from './pages/v11/V11Routes';

import { Toaster } from '@/components/ui/toaster';
import { Toaster as SonnerToaster } from '@/components/ui/sonner';

const ProtectedLayout = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  // Version access control
  const currentPath = location.pathname;
  let currentVersion: AppVersion | null = null;

  if (currentPath.startsWith('/v11/')) currentVersion = 'v11';
  else if (currentPath.startsWith('/login') || currentPath.startsWith('/register')) {
    currentVersion = null; // Public routes
  }

  // Redirect if accessing blocked version
  if (currentVersion && !versionConfig.isVersionAllowed(currentVersion)) {
    const redirectTo = versionConfig.getDefaultRoute();

    if (import.meta.env.DEV) {
      console.warn(
        `[Version Guard] Blocked access to ${currentVersion}. ` + `Redirecting to ${redirectTo}`
      );
    }

    return <Navigate to={redirectTo} replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <DateRangeProvider>
      <Routes>
        {/* V11 Routes - Felton Intelligence */}
        <Route
          path="/v11/*"
          element={
            <V11Layout>
              <V11Routes />
            </V11Layout>
          }
        />
        {/* Fallback: redirect any unknown routes to default version */}
        <Route path="*" element={<Navigate to={versionConfig.getDefaultRoute()} replace />} />
      </Routes>
    </DateRangeProvider>
  );
};

const AppRoutes = () => {
  usePageTitle();

  return (
    <Routes>
      <Route path="/login" element={<NewLoginPage />} />
      {/* Registration temporarily disabled - uncomment to re-enable */}
      {/* <Route path="/register" element={<RegisterPage />} /> */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      {/* Redirect root to login */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      {/* Rotas protegidas com o Layout */}
      <Route path="/*" element={<ProtectedLayout />} />
    </Routes>
  );
};

function App() {
  const { resolvedTheme } = useTheme();
  const queryClient = useQueryClient();

  // One-time cleanup of migration artifacts
  useEffect(() => {
    // Clean up localStorage keys from datasource migration
    const keysToClean = Object.keys(localStorage).filter(
      key => key.startsWith('platform_') || key.startsWith('analysis_end_')
    );

    keysToClean.forEach(key => localStorage.removeItem(key));

    // Set migration flag to prevent re-run
    if (!localStorage.getItem('datasources_migration_v1')) {
      localStorage.setItem('datasources_migration_v1', 'complete');
    }

    // Clear old API caches
    queryClient.removeQueries({ queryKey: ['account-competitors'] });
    queryClient.removeQueries({ queryKey: ['account-partners'] });
    queryClient.removeQueries({ queryKey: ['account-influencers'] });
    queryClient.removeQueries({ queryKey: ['main-channel'] });

    // Force refetch of new unified API
    queryClient.invalidateQueries({ queryKey: ['datasources'] });
    queryClient.invalidateQueries({ queryKey: ['channel-groups'] });
  }, [queryClient]);

  // Cria um tema MUI dinâmico que reage às mudanças do nosso ThemeContext.
  // Tem de ser o tema já resolvido: 'system' não é uma paleta, e tratá-lo como
  // claro punha os componentes MUI em claro sobre o fundo escuro do Tailwind.
  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: resolvedTheme,
        },
        // AL-1849: without this, CssBaseline put MUI's default (Roboto, with
        // 0.15px tracking) on <body> and it won over index.css's Inter, so most
        // of the app rendered in Roboto — or its Helvetica fallback.
        typography: {
          fontFamily: '"Inter", system-ui, sans-serif',
          body1: { letterSpacing: 'normal' },
          body2: { letterSpacing: 'normal' },
        },
      }),
    [resolvedTheme]
  );

  return (
    <MuiThemeProvider theme={muiTheme}>
      <GlobalStyles styles={globalStyles} />
      <CssBaseline />
      <GoogleAuthProviderWrapper>
        <UserProfileProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <FilterProvider>
              <Router>
                <AppRoutes />
              </Router>
            </FilterProvider>
          </LocalizationProvider>
        </UserProfileProvider>
      </GoogleAuthProviderWrapper>
      {/* Two toast systems live side by side: the Radix one for
          `@/hooks/use-toast`, sonner for `@/components/ui/use-toast` and
          direct `sonner` calls (AL-1861 — sonner was never mounted). */}
      <Toaster />
      <SonnerToaster />
    </MuiThemeProvider>
  );
}

export default App;
